import { useMemo, useState } from 'react';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Button from '../components/button/Button';
import Modal from '../components/modal/modal';
import P from '../components/texts/p/P';
import {
  exportBcraCsv,
  exportContingenteCsv,
  exportRiesgoVivoCsv,
  exportVistaCierreCsv,
} from '../services/GuaranteesService';
import { extractFilenameFromContentDisposition } from '../utils/HttpUtils';

type ReportKey = 'riesgoVivo' | 'contingente' | 'bcra' | 'vistaCierre';

type ReportDef = {
  key: ReportKey;
  title: string;
  requiresDate: boolean;
};

const REPORTS: ReportDef[] = [
  { key: 'riesgoVivo', title: 'Detalle de Riesgo Vivo', requiresDate: true },
  { key: 'contingente', title: 'Detalle del contingente por aval', requiresDate: false },
  { key: 'bcra', title: 'Informe BCRA', requiresDate: false },
  { key: 'vistaCierre', title: 'Vista cierre', requiresDate: true },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const downloadBlob = (data: ArrayBuffer | Blob, filename: string) => {
  const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const Reportes = () => {
  const [exportingKey, setExportingKey] = useState<ReportKey | null>(null);
  const [dateModalReport, setDateModalReport] = useState<ReportDef | null>(null);
  const [fechaConsulta, setFechaConsulta] = useState(todayIso());

  const isDateValid = useMemo(() => Boolean(fechaConsulta && /^\d{4}-\d{2}-\d{2}$/.test(fechaConsulta)), [fechaConsulta]);

  const runExport = async (report: ReportDef, fecha?: string) => {
    setExportingKey(report.key);
    try {
      let result: any;
      switch (report.key) {
        case 'riesgoVivo':
          result = await exportRiesgoVivoCsv(fecha!);
          break;
        case 'contingente':
          result = await exportContingenteCsv();
          break;
        case 'bcra':
          result = await exportBcraCsv();
          break;
        case 'vistaCierre':
          result = await exportVistaCierreCsv(fecha!);
          break;
      }
      if (result && 'data' in result) {
        const fromHeader = extractFilenameFromContentDisposition(result.headers?.['content-disposition']);
        const fallback =
          report.key === 'riesgoVivo'
            ? `detalle-riesgo-vivo_${fecha}.csv`
            : report.key === 'vistaCierre'
              ? `vista-cierre_${fecha}.csv`
              : report.key === 'bcra'
                ? `informe-bcra_${todayIso()}.csv`
                : `detalle-contingente-por-aval_${todayIso()}.csv`;
        downloadBlob(result.data, fromHeader || fallback);
      }
    } catch (error) {
      console.error(`Error exporting report ${report.key}:`, error);
    }
    setExportingKey(null);
  };

  const handleDownloadClick = (report: ReportDef) => {
    if (report.requiresDate) {
      setFechaConsulta(todayIso());
      setDateModalReport(report);
      return;
    }
    void runExport(report);
  };

  const handleConfirmDateDownload = async () => {
    if (!dateModalReport || !isDateValid) return;
    const report = dateModalReport;
    setDateModalReport(null);
    await runExport(report, fechaConsulta);
  };

  return (
    <Card variant='neutral' modifier='inverted' spacing='4' height='-webkit-fill-available' width='85vw'>
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Reportes
        </H>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {REPORTS.map((report) => (
          <div key={report.key} style={{ border: '1px solid #DFE6EB', borderRadius: '8px', padding: '16px' }}>
            <Card display='flex' justifyContent='space-between' alignItems='center'>
              <H withMargin={false} variant='secondary-2' type='2' weight='bold' modifier='strong'>
                {report.title}
              </H>
              <Button
                variant='primary'
                size='lg'
                onClick={() => handleDownloadClick(report)}
                disabled={exportingKey === report.key}
              >
                {exportingKey === report.key ? 'Descargando...' : 'Descargar'}
              </Button>
            </Card>
          </div>
        ))}
      </div>

      <Modal
        isOpen={Boolean(dateModalReport)}
        title='Fecha de consulta'
        primaryText='Descargar'
        secondaryText='Cancelar'
        primaryActionDisabled={!isDateValid || exportingKey != null}
        loadingPrimaryBtn={exportingKey != null}
        onPrimaryAction={handleConfirmDateDownload}
        onSecondaryAction={() => setDateModalReport(null)}
        onClose={() => setDateModalReport(null)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 0' }}>
          <P withMargin={false} type='2'>
            Seleccioná la fecha de consulta para {dateModalReport?.title ?? 'el reporte'}.
          </P>
          <input
            type='date'
            value={fechaConsulta}
            onChange={(e) => setFechaConsulta(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #DFE6EB' }}
          />
        </div>
      </Modal>
    </Card>
  );
};

export default Reportes;
